/**
 * Server functions for CIH back-references from a coach's public profile.
 * Powers the "Frameworks this coach uses", "Challenges this coach helps with",
 * and "Writing by this coach" sections — the reverse of the links exposed on
 * CIH framework / challenge / article pages.
 *
 * Aliases: coach cih server fns, coach back refs, coach reverse lookup
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import type { CoachCihBackRefs } from "~/lib/server/cih/coaches";

export const getCoachCihBackRefsFn = createServerFn({ method: "GET" })
  .validator(z.object({ coachId: z.string().uuid() }))
  .handler(async ({ data }): Promise<CoachCihBackRefs> => {
    const { getCoachCihBackRefs } = await import("~/lib/server/cih/coaches");
    return getCoachCihBackRefs(data.coachId);
  });

export const coachCihBackRefsQueryOptions = (coachId: string) =>
  queryOptions({
    queryKey: ["coach", "cih-back-refs", coachId],
    queryFn: () => getCoachCihBackRefsFn({ data: { coachId } }),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled: coachId.length > 0,
  });
